import { useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
// contexts
import { AuthProvider } from "./AuthContext";
import { AutomationsProvider } from "./AutomationsContext";
import { ProductsProvider } from "./ProductsContext";
import { ProductsProvider as ProductsProviderV2 } from "./V2/ProductsContext";
import { OrdersProvider } from "./OrdersContext";
import { SettingsProvider } from "./SettingsContext";
import { ShopProvider } from "./ShopContext";
import { StatsProvider } from "./StatsContext";
import { BISNotificationsProvider } from "./BackInStockContext";
import { IntegrationsProvider } from "./IntergrationsContext";
import { SellingPlansProvider } from "./SellingPlansContext";
import { ActivityLogsProvider } from "./ActivityLogsContext";
import { AutomationTagsProvider } from "./AutomationTagsContext";
// constants
import { APP_ROUTES, TIMESACT_VERSION_2 } from "../common/constants";
import { VersionStoreContext, VersionStoreProvider } from "./VersionStoreContext";
import { ShopProvider as ShopProviderV2 } from "./V2/ShopContext";
import { CustomerExperiencesProvider } from "./V2/CustomerExperiencesContext";
import { NotificationProvider } from "./V2/NotificationContext";
import { ReportsProvider } from "./V2/ReportsContext";
import { SubscriptionProvider } from "./V2/SubscriptionContext";
import { Spinner } from "@shopify/polaris";

const SKIP_INIT_CONTEXTS_ROUTES = [APP_ROUTES.CUSTOMERS];

const combineProviders = (...components) => {
  return components.reduce(
    (AccumulatedProvider, CurrentProvider) => {
      return ({ children }) => (
        <AccumulatedProvider>
          <CurrentProvider>{children}</CurrentProvider>
        </AccumulatedProvider>
      );
    },
    ({ children }) => <>{children}</>
  );
};

// V1 Providers (Firebase)
const V1Providers = combineProviders(
  AuthProvider,
  StatsProvider,
  ShopProvider,
  ActivityLogsProvider,
  ProductsProvider,
  SettingsProvider,
  OrdersProvider,
  AutomationsProvider,
  BISNotificationsProvider,
  IntegrationsProvider,
  SellingPlansProvider,
  AutomationTagsProvider
);

// V2 Providers (DB)
const V2Providers = combineProviders(
  AuthProvider,
  ShopProviderV2,
  ProductsProviderV2,
  CustomerExperiencesProvider,
  NotificationProvider,
  ReportsProvider,
  SubscriptionProvider
);

const AppContextConsumer = ({ children }) => {
  const { pathname } = useLocation();
  const { timesactVersion, isVersionLoaded } = useContext(VersionStoreContext);

  const memoizedV1 = useMemo(() => <V1Providers>{children}</V1Providers>, [children]);
  const memoizedV2 = useMemo(() => <V2Providers>{children}</V2Providers>, [children]);

  if (SKIP_INIT_CONTEXTS_ROUTES.some((route) => pathname.includes(route))) {
    return <>{children}</>;
  }

  // Show loading state while version is being determined
  if (!isVersionLoaded) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <Spinner accessibilityLabel="Loading store configuration" size="large" />
      </div>
    );
  }

  if (timesactVersion && timesactVersion === TIMESACT_VERSION_2) {
    console.log("V2 STORE LOAD");
    return memoizedV2;
  } else {
    console.log("V1 SHOP LOAD");
    return memoizedV1;
  }
};

export const AppContextProvider = ({ children }) => (
  <VersionStoreProvider>
    <AppContextConsumer>{children}</AppContextConsumer>
  </VersionStoreProvider>
);
